/**
 * Zod schemas for the auth forms. These mirror the backend's own
 * validation (backend/internal/handlers/auth.go +
 * services/auth/password.go) so the client surfaces obvious errors
 * inline before a round-trip. The backend remains authoritative — its
 * { error, message, field } responses are still wired to fields, and
 * server-only checks (e.g. breached-password) come back as field errors.
 */

import { z } from "zod";

// Mirrors auth.ValidatePassword: >=12 chars, <=256, >=3 of 4 classes
// (lower, upper, digit, symbol). The breach denylist is server-only.
function passwordClassCount(p: string): number {
  let lower = false;
  let upper = false;
  let digit = false;
  let symbol = false;
  for (const ch of p) {
    if (/[a-z]/.test(ch)) lower = true;
    else if (/[A-Z]/.test(ch)) upper = true;
    else if (/[0-9]/.test(ch)) digit = true;
    else symbol = true; // punctuation, symbols, space
  }
  return [lower, upper, digit, symbol].filter(Boolean).length;
}

export const passwordSchema = z
  .string()
  .min(12, "Must be at least 12 characters")
  .max(256, "Must be at most 256 characters")
  .refine(
    (p) => passwordClassCount(p) >= 3,
    "Use at least 3 of: lowercase, uppercase, digit, symbol",
  );

const emailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .min(1, "Email is required")
  .email("Enter a valid email address");

// ID.2 identity auth: login is email + password only. There is no
// company_slug (and no workspace-required variant) — the active
// company is established AFTER auth via /auth/activate.
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, "Password is required"),
});
export type LoginValues = z.infer<typeof loginSchema>;

// 2026-06-02 UX fix: company_slug is no longer asked at register time
// — the backend auto-derives it from company_name. Users rename it
// later in Settings → Workspace with a stability warning. Schema kept
// as an OPTIONAL field (no length/regex check) so legacy callers that
// still send it are accepted unchanged; the SPA register form just
// stops rendering the input.
export const registerSchema = z.object({
  company_name: z.string().trim().min(1, "Company name is required"),
  // Optional workspace handle (lifecycle walkthrough 2026-10): "" lets the
  // server derive one from the company name; otherwise the server's rule.
  company_slug: z
    .string()
    .trim()
    .toLowerCase()
    .refine((v) => v === "" || /^[a-z][a-z0-9-]{2,62}$/.test(v), {
      message: "3–63 characters: lowercase letters, numbers and hyphens, starting with a letter",
    })
    .optional(),
  contact_phone: z.string().trim().optional().or(z.literal("")),
  first_name: z.string().trim().min(1, "First name is required"),
  last_name: z.string().trim().min(1, "Last name is required"),
  email: emailSchema,
  password: passwordSchema,
});
export type RegisterValues = z.infer<typeof registerSchema>;

// ID.2 "add a company" (a logged-in identity attaching another
// company). The backend keys the attach on the SESSION identity and
// ignores the posted password (the identity already has a credential),
// so the client must not enforce password strength. `password` stays a
// required `string` (so the inferred type is IDENTICAL to
// registerSchema and the resolvers are interchangeable) — it's just no
// longer length/complexity-checked; the page sends "" in add-mode.
export const addCompanySchema = registerSchema.extend({
  password: z.string(),
});

// ID.2: forgot/reset operate on the IDENTITY (one password,
// account-wide) — no company_slug.
export const forgotSchema = z.object({
  email: emailSchema,
});
export type ForgotValues = z.infer<typeof forgotSchema>;

export const resetSchema = z
  .object({
    email: emailSchema,
    token: z.string().trim().min(1, "Reset token is required"),
    new_password: passwordSchema,
    confirm_password: z.string().min(1, "Confirm your new password"),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    message: "Passwords do not match",
    path: ["confirm_password"],
  });
export type ResetValues = z.infer<typeof resetSchema>;

// 6-digit TOTP code (authenticator).
export const totpCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .regex(/^\d{6}$/, "Enter the 6-digit code from your authenticator"),
});
export type TotpCodeValues = z.infer<typeof totpCodeSchema>;
